// src/pages/DashboardPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FolderPlusIcon,
  ChartBarIcon,
  PhotoIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();

  const quickActions = [
    {
      name: 'Create New Batch',
      description: 'Start processing images for alt-text generation',
      href: '/batches',
      icon: FolderPlusIcon,
      color: 'bg-blue-500'
    },
    {
      name: 'View All Batches',
      description: 'Manage your existing image batches',
      href: '/batches',
      icon: PhotoIcon,
      color: 'bg-green-500'
    },
    {
      name: 'Profile Settings',
      description: 'Update your account information',
      href: '/profile',
      icon: CogIcon,
      color: 'bg-purple-500'
    }
  ];

  const adminActions = [
    {
      name: 'Admin Dashboard',
      description: 'View system statistics and analytics',
      href: '/admin',
      icon: ChartBarIcon,
      color: 'bg-red-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="card">
        <div className="card-content pt-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-gray-600">
            Ready to generate accessible alt-text for your images? Here's what you can do:
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="card hover:shadow-lg transition-shadow duration-200"
            >
              <div className="card-content pt-6">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${action.color} text-white mr-4`}>
                    <action.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{action.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{action.description}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Admin Section */}
      {isAdmin() && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Administration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminActions.map((action) => (
              <Link
                key={action.name}
                to={action.href}
                className="card hover:shadow-lg transition-shadow duration-200"
              >
                                  <div className="card-content pt-6">
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg ${action.color} text-white mr-4`}>
                      <action.icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{action.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{action.description}</p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Getting Started Guide */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Getting Started</h2>
        </div>
        <div className="card-content pt-6">
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-sm font-medium text-blue-600">1</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Create a New Batch</h3>
                <p className="text-sm text-gray-500">Start by creating a batch and configuring your preferences for language and alt-text length.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-sm font-medium text-blue-600">2</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Upload Images</h3>
                <p className="text-sm text-gray-500">Add your images to the batch. Supported formats: JPEG, PNG, GIF, BMP, WebP, TIFF, SVG.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-sm font-medium text-blue-600">3</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Process & Download</h3>
                <p className="text-sm text-gray-500">Start processing and monitor progress in real-time. Download your results as an Excel file when complete.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;